import { $getRoot, $insertNodes, type LexicalEditor } from 'lexical';

import {
  type FunctionComponent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Barrier,
  Button,
  CustomDropdown,
  Input,
  TextArea,
  ThemeProvider,
  time,
} from '@dr.pogodin/react-utils';

import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';

import { ListItemNode, ListNode } from '@lexical/list';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HeadingNode } from '@lexical/rich-text';

import { captureException } from '@sentry/react';

import Throbber from 'components/Throbber';
import ImageOrVideoUpload from 'components/admin/ImageOrVideoUpload';
import RemoveBlogItemModal from 'components/admin/RemoveBlogItemModal';
import RequiredField from 'components/admin/RequiredField';

import {
  type BlogItemStatusT,
  type BlogItemT,
  P_WEBSITE_BLOG,
  evalSummaryError,
  evalTitleError,
  getConfig,
  getId,
  isVimeoUrl,
} from 'utils';

import customDropdownTheme from 'themes/CustomDropdown/admin/with-inner-label-60px.scss';
import inputTheme from 'themes/Input/admin/with-inner-label-60px.scss';

import LexBlogTheme from 'themes/Lexical/blog.scss';

import Toolbar from './Toolbar';

import flexInput from './input.scss';
import S from './style.scss';

const editorTheme = {
  heading: { h2: LexBlogTheme.h2 },
  list: {
    listitem: LexBlogTheme.list,
  },
  paragraph: LexBlogTheme.paragraph,
};

const CATEGORIES = [
  'The Science',
  'Practitioner Wisdom',
  'Research Studies',
  'Community Stories',
];

const STATUS_OPTIONS: Array<{
  name: string;
  value: BlogItemStatusT;
}> = [{
  name: 'Draft',
  value: 'DRAFT',
}, {
  name: 'Scheduled',
  value: 'SCHEDULED',
}, {
  name: 'Published',
  value: 'PUBLISHED',
}, {
  name: 'Delete',
  value: 'DELETED',
}];

async function getHtml(
  editorRef: RefObject<LexicalEditor | null>,
): Promise<string> {
  const editor = editorRef.current;
  if (!editor) throw Error('Editor is not ready');

  const promise = new Barrier<string>();

  editor.read(() => {
    void promise.resolve($generateHtmlFromNodes(editor, null));
  });

  return promise;
}

function setHtml(
  html: string,
  editorRef: RefObject<LexicalEditor | null>,
): void {
  const editor = editorRef.current;
  if (!editor) throw Error('Editor is not ready');

  editor.update(() => {
    const parser = new DOMParser();
    const dom = parser.parseFromString(html, 'text/html');
    const nodes = $generateNodesFromDOM(editor, dom);

    $getRoot().clear();
    $insertNodes(nodes);
  });
}

const DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm';

type PropsT = {
  initialState?: BlogItemT;
  save: (
    item: BlogItemT,
    image: File | string | undefined,
    onProgress: (progress: number) => void,
  ) => Promise<BlogItemT>;
};

const BlogItemEditor: FunctionComponent<PropsT> = ({
  initialState,
  save,
  // eslint-disable-next-line complexity
}) => {
  const [busy, setBusy] = useState<boolean | number>(false);

  const editorRef = useRef<LexicalEditor>(null);

  const [title, setTitle] = useState(initialState?.title ?? '');
  const [titleError, setTitleError] = useState('');

  const [summary, setSummary] = useState(initialState?.summary ?? '');
  const [summaryError, setSummaryError] = useState('');

  const [category, setCategory] = useState(
    initialState?.category ?? CATEGORIES[0] ?? '',
  );

  const [coverMedia, setCoverMedia] = useState<File | string>();
  const [coverMediaError, setCoverMediaError] = useState<string>();

  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const [status, setStatus] = useState<BlogItemStatusT>(
    initialState?.status ?? 'DRAFT',
  );

  const now = time().format(DATE_TIME_FORMAT);

  const [publishedAt, setPublishedAt] = useState(
    initialState?.publishedAt
      ? time(initialState.publishedAt).format(DATE_TIME_FORMAT)
      : now,
  );

  const onSave = async (): Promise<string> => {
    try {
      setBusy(true);

      const content = await getHtml(editorRef);

      const res = await save({
        category,
        content,
        publishedAt: time(publishedAt).valueOf(),
        status,
        summary,
        title,

        // eslint-disable-next-line no-underscore-dangle
        __v: initialState?.__v,
      }, coverMedia, setBusy);

      setCategory(res.category);
      setCoverMedia(undefined);

      setHtml(res.content, editorRef);

      setPublishedAt(
        res.publishedAt ? time(res.publishedAt).format(DATE_TIME_FORMAT) : now,
      );

      setStatus(res.status);
      setSummary(res.summary);
      setTitle(res.title);

      return getId(res);
    } catch (error) {
      captureException(error);
      // eslint-disable-next-line no-alert
      alert('Failed to save');
    } finally {
      setBusy(false);
    }

    return '';
  };

  const canSave: boolean = !evalTitleError(title)
    && !evalSummaryError(summary)
    && !!(coverMedia ?? initialState?.image);

  useEffect(() => {
    if (initialState?.content) {
      setHtml(initialState.content, editorRef);
    }
  }, [initialState?.content]);

  const config = getConfig(true);

  // TODO: If we stay on the page, and the head media is a video,
  // and it is not yet ready to be played... we need to periodically
  // refresh the ready to play status in the background.

  let remoteMedia: {
    isReadyToBePlayed: boolean;
    url: string;
  } | string;

  if (initialState?.image && isVimeoUrl(initialState.image)) {
    remoteMedia = {
      isReadyToBePlayed: initialState.isHeadMediaReady ?? false,
      url: initialState.image,
    };
  } else remoteMedia = initialState?.image ?? '';

  return (
    <ThemeProvider
      themes={{
        CustomDropdown: customDropdownTheme,
        Input: inputTheme,
      }}
    >
      <div className={S.container}>
        <div styleName="inputBlock">
          <Input
            error={titleError}
            label="Title"
            onBlur={() => {
              const error = evalTitleError(title);
              if (error) setTitleError(error);
            }}
            onChange={(e) => {
              const { value } = e.target;
              setTitle(value);
              if (titleError && !evalTitleError(value)) setTitleError('');
            }}
            value={title}
          />
          {titleError ? null : <RequiredField />}
        </div>
        <div styleName="inputBlock">
          <TextArea
            error={summaryError}
            label="Brief Summary (Plain Text, 50–300 characters)"
            onBlur={() => {
              const error = evalSummaryError(summary);
              if (error) setSummaryError(error);
            }}
            onChange={(e) => {
              const { value } = e.target;
              setSummary(value);
              if (summaryError && !evalSummaryError(value)) {
                setSummaryError('');
              }
            }}
            value={summary}
          />
          {summaryError ? null : <RequiredField />}
        </div>
        <div styleName="row">
          <CustomDropdown
            label="Category"
            onChange={(value) => {
              setCategory(value as string);
            }}
            options={CATEGORIES}
            theme={flexInput}
            value={category}
          />
          <CustomDropdown
            label="Status"
            onChange={(value) => {
              if (value === 'DELETED' satisfies BlogItemStatusT) {
                setShowRemoveModal(true);
              } else setStatus(value as BlogItemStatusT);
            }}
            options={
              initialState
                ? STATUS_OPTIONS
                : STATUS_OPTIONS.filter((item) => item.value !== 'DELETED')
            }
            theme={flexInput}
            value={status}
          />
          <Input
            disabled={status !== 'SCHEDULED'}
            label="Published / Scheduled"
            min={now}
            onChange={(e) => {
              setPublishedAt(e.target.value);
            }}
            theme={flexInput}
            type="datetime-local"
            value={publishedAt}
          />
        </div>
        <div styleName="coverImageBlock">
          <div styleName="headLabels">
            <div>Upload a cover image or video</div>
            <RequiredField />
          </div>
          <ImageOrVideoUpload
            error={coverMediaError}
            localMedia={coverMedia}
            remoteMedia={remoteMedia}
            setLocalMedia={(neu) => {
              const hasMedia = neu ?? initialState?.image;
              setCoverMediaError(
                hasMedia ? undefined : 'A cover image or video is required',
              );
              setCoverMedia(neu);
            }}
          />
        </div>
        <LexicalComposer
          initialConfig={{
            // editorState: initialState?.content,
            namespace: 'blogEditor',
            nodes: [HeadingNode, ListItemNode, ListNode],
            onError: (error) => {
              captureException(error);

              // eslint-disable-next-line no-console
              console.error(error);
            },
            theme: editorTheme,
          }}
        >
          <div styleName="mainEditorContainer">
            <Toolbar />
            <div styleName="mainEditorContentWrapper">
              <RichTextPlugin
                ErrorBoundary={LexicalErrorBoundary}
                contentEditable={(
                  <ContentEditable
                    aria-placeholder="Enter some text..."
                    placeholder={<div styleName="placeholder">Enter some text...</div>}
                    styleName="mainEditorContent"
                  />
                )}
              />
            </div>
          </div>
          <EditorRefPlugin editorRef={editorRef} />
          <HistoryPlugin />
          <AutoFocusPlugin />
          <ListPlugin />
        </LexicalComposer>
        <div styleName="buttons">
          <Button
            disabled={!canSave}
            onClick={() => {
              void (async () => {
                const id = await onSave();
                if (!id) {
                  // eslint-disable-next-line no-alert
                  alert('Something has failed');
                  return;
                }
                window.open(
                  `${config.HTTP_SCHEMA}${config.DOMAIN}${P_WEBSITE_BLOG}/${id}`,
                  '_blank',
                );
              })();
            }}
          >
            Preview
          </Button>
          <Button
            disabled={!canSave}
            onClick={() => {
              void onSave();
            }}
          >
            Save
          </Button>
        </div>
        {busy ? (
          <Throbber
            progress={typeof busy === 'number' ? Math.floor(100 * busy) : undefined}
            withOverlay
          />
        ) : null}
        {showRemoveModal ? (
          <RemoveBlogItemModal
            blogId={getId(initialState)}
            onCancel={() => {
              setShowRemoveModal(false);
            }}
          />
        ) : null}
      </div>
    </ThemeProvider>
  );
};

export default BlogItemEditor;
